/**
 * Pure derivations for the schedule (Gantt) dialog: date parsing, provenance →
 * marker state, drag-clamp neighbor bounds, the overlay of working overrides onto a
 * payload row, the collapse/filter-aware row model, the axis domain (with the
 * pre-1900 garbage-date clamp), and the ms→percent projection the DOM Gantt tracks
 * position bars and markers with. Everything here is a pure function so it is
 * unit-tested without a DOM.
 */

import moment from 'moment';
import {
  AXIS_MIN_ISO,
  MILESTONE_LABELS,
  MILESTONE_ORDER,
  MILESTONE_WIRE_FIELD,
  PHASE_BOUNDS,
  PHASE_ORDER,
  PRODUCING_TAIL_MONTHS,
  UNSCHEDULED_GROUP_NAME,
} from './constants';
import type {
  ScheduleGroup,
  FilteredScheduleRow,
  GroupHeader,
  GroupSummaryBand,
  MarkerState,
  MergedDate,
  MilestoneKey,
  PhaseKey,
  PhaseSegment,
  ScheduleOverrideEntry,
  ScheduleRow,
  ScheduleRowModel,
  SlotId,
  VisibleRow,
} from './types';

// ---------------------------------------------------------------------------
// Dates (ISO day ⇄ epoch ms, all UTC — no time component ever)
// ---------------------------------------------------------------------------

/**
 * Parses an ISO date string to UTC start-of-day epoch ms, or null.
 *
 * @param {string | null | undefined} value - ISO date (day precision).
 * @return {number | null} Epoch ms at UTC midnight, or null.
 */
export function parseIsoDay(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const m = moment.utc(value, moment.ISO_8601, true);
  return m.isValid() ? m.startOf('day').valueOf() : null;
}

/**
 * Formats UTC epoch ms back to an ISO day string (`YYYY-MM-DD`).
 *
 * @param {number} ms - Epoch ms.
 * @return {string} ISO day.
 */
export function toIsoDay(ms: number): string {
  return moment.utc(ms).format('YYYY-MM-DD');
}

/** UTC epoch ms for the pre-1900 domain floor. */
export const AXIS_MIN_MS = parseIsoDay(AXIS_MIN_ISO) as number;

/**
 * Shifts an ISO day by a whole number of months (bulk shift). Negative = earlier.
 *
 * @param {string} value - ISO day.
 * @param {number} months - Signed month offset.
 * @return {string} The shifted ISO day.
 */
export function shiftIsoMonths(value: string, months: number): string {
  return moment.utc(value).add(months, 'months').format('YYYY-MM-DD');
}

/**
 * Snaps a UTC epoch-ms to the START of its month — the month boundary the calculator models
 * to (`constraints.monthStartModeled`). A dragged chart marker / picked table date is a
 * user-chosen instant on a continuous axis, so without this it can land mid-month (e.g.
 * 05/25) while every modeled date sits on the 1st (Defect 5 / INS-11293).
 *
 * @param {number} ms - Epoch ms.
 * @return {number} Epoch ms at the 1st of that month (UTC midnight).
 */
export function snapMsToMonth(ms: number): number {
  return moment.utc(ms).startOf('month').valueOf();
}

/**
 * Snaps an ISO day to the 1st of its month — the ISO-string form of {@link snapMsToMonth}.
 * An unparseable value passes through untouched.
 *
 * @param {string} iso - ISO day.
 * @return {string} The 1st-of-month ISO day.
 */
export function snapIsoToMonth(iso: string): string {
  const ms = parseIsoDay(iso);
  return ms === null ? iso : toIsoDay(snapMsToMonth(ms));
}

// ---------------------------------------------------------------------------
// Selection keys
// ---------------------------------------------------------------------------

/**
 * The selection identity for one milestone point: `slotId|field`.
 *
 * @param {SlotId} slotId - The slot.
 * @param {MilestoneKey} field - The milestone field.
 * @return {string} The composite key.
 */
export function selectionKey(slotId: SlotId, field: MilestoneKey): string {
  return `${slotId}|${field}`;
}

/**
 * Parses a `slotId|field` selection key back into its parts.
 *
 * @param {string} key - The composite key.
 * @return {object} The parts.
 */
export function parseSelectionKey(key: string): {
  slotId: SlotId;
  field: MilestoneKey;
} {
  const idx = key.lastIndexOf('|');
  return {
    slotId: key.slice(0, idx),
    field: key.slice(idx + 1) as MilestoneKey,
  };
}

// ---------------------------------------------------------------------------
// Provenance → marker state / editability
// ---------------------------------------------------------------------------

/**
 * The three-state marker for a merged date, from its `source` alone (spec 5.2).
 *
 * ── WHY A FOURTH SOURCE STILL MAPS TO THREE MARKERS ──────────────────────────────────
 *
 * `derivedFromFirstProduction` is a completion date the schedule blob does not carry
 * (scheduled_date_merger.rb DERIVED_COMPLETION). The MARKER encodes what the reader can DO
 * with a point — modelled and draggable, or reported and locked — and on that question a
 * derived date is exactly a modelled one: not reported, not locked, editable. So it draws as
 * `scheduled` and the Gantt's three shapes are unchanged.
 *
 * The finer provenance is not lost, it is carried where it belongs: the Table tab's and the
 * export's source-annotation column has its own fourth value (table-model.ts
 * SOURCE_ANNOTATION), which is where a reader goes to ask WHERE a date came from rather than
 * what they may do to it. Widening MarkerState instead would mean a fourth marker shape and
 * a Gantt legend change, which is a design decision and not a consequence of this fix.
 *
 * @param {MergedDate} date - The merged date.
 * @return {MarkerState | null} The marker state, or null when there is no value.
 */
export function markerState(date: MergedDate): MarkerState | null {
  if (date.value === null || date.source === null) {
    return null;
  }
  return date.source === 'derivedFromFirstProduction'
    ? 'scheduled'
    : date.source;
}

/**
 * Whether a milestone point can be dragged/edited: it has a value and is not locked
 * (reported). Unscheduled/null milestones have no point to drag.
 *
 * @param {MergedDate} date - The merged date.
 * @return {boolean} Editable.
 */
export function isEditablePoint(date: MergedDate): boolean {
  return date.value !== null && !date.locked;
}

/**
 * Maps a milestone key to its snake_case calculate-wire field name.
 *
 * @param {MilestoneKey} field - The milestone key.
 * @return {string} The wire field name.
 */
export function milestoneToWireField(field: MilestoneKey): string {
  return MILESTONE_WIRE_FIELD[field];
}

// ---------------------------------------------------------------------------
// Working-override overlay
// ---------------------------------------------------------------------------

/**
 * Overlays the working-copy overrides for a slot onto its base payload dates. A
 * string override flips the field to `source: 'override'` (unless the base is
 * locked/reported, which is never overridable — the client prevents it); a null
 * override or a missing field falls back to the base merged date.
 *
 * @param {ScheduleRow} row - The base payload row.
 * @param {ScheduleOverrideEntry[]} entries - The working-copy entries.
 * @return {object} The effective dates.
 */
export function overlayRowDates(
  row: ScheduleRow,
  entries: ScheduleOverrideEntry[]
): Record<MilestoneKey, MergedDate> {
  const result = { ...row.dates } as Record<MilestoneKey, MergedDate>;
  for (const entry of entries) {
    if (entry.slotId !== row.slotId) {
      continue;
    }
    for (const field of MILESTONE_ORDER) {
      if (!(field in entry.overrides)) {
        continue;
      }
      const value = entry.overrides[field];
      const base = row.dates[field];
      // Reported/locked dates are immutable — never overlay onto them.
      if (base?.locked) {
        continue;
      }
      if (value === null || value === undefined) {
        result[field] = base;
      } else {
        result[field] = { value, source: 'override', locked: false };
      }
    }
  }
  return result;
}

/**
 * Whether a row carries any `override`-sourced effective date (drives the row-level
 * "edited" adornment).
 *
 * @param {object} dates - Effective dates.
 * @return {boolean} Edited.
 */
export function rowIsEdited(dates: Record<MilestoneKey, MergedDate>): boolean {
  return MILESTONE_ORDER.some((field) => dates[field]?.source === 'override');
}

/**
 * Whether every present milestone on a row is reported/locked ("All dates reported").
 *
 * @param {object} dates - Effective dates.
 * @return {boolean} Fully locked.
 */
export function rowIsFullyLocked(
  dates: Record<MilestoneKey, MergedDate>
): boolean {
  const present = MILESTONE_ORDER.filter((f) => dates[f]?.value !== null);
  return present.length > 0 && present.every((f) => dates[f].locked);
}

// ---------------------------------------------------------------------------
// Drag clamp (neighbor bounds mirror the BE lifecycle-order constraint)
// ---------------------------------------------------------------------------

/**
 * The [min, max] epoch-ms bounds a milestone may be dragged to, from its nearest
 * present neighbors in lifecycle order. Order is `≤`, so bounds are inclusive (equal
 * dates are allowed and form a stack). Missing neighbors leave that side unbounded
 * (`±Infinity`); callers additionally clamp to the axis domain.
 *
 * @param {object} dates - The row's effective dates.
 * @param {MilestoneKey} field - The milestone being dragged.
 * @param {MilestoneKey[]} order - The lifecycle order (constraints.order).
 * @return {object} The inclusive drag bounds.
 */
export function neighborBounds(
  dates: Record<MilestoneKey, MergedDate>,
  field: MilestoneKey,
  order: MilestoneKey[] = MILESTONE_ORDER
): {
  minMs: number;
  minField?: MilestoneKey;
  maxMs: number;
  maxField?: MilestoneKey;
} {
  // ── THE BINDING NEIGHBOUR, NOT THE NEAREST ONE ────────────────────────────────────────
  //
  // This used to stop at the first dated field walking outwards. That is the right answer on
  // a row that is already in order and the wrong one when it is not: with permit 2030 and
  // spud 2020 the nearest lower bound for completion is 2020, so 2021 passed while sitting
  // nine years before its own permit date. Measured on sandbox — slot 4222742404 carries a
  // completion three months after its first production — so disordered rows are real.
  //
  // Every dated field on the side now participates and the TIGHTEST wins. On an ordered row
  // that is the same field the walk returned, so the schedule dialog's drag clamps are
  // unchanged there; on a disordered one they become stricter, which is the point — and the
  // field that won is returned beside the number, because it is no longer the nearest one and
  // a caller walking outward would name a different date than the bound it is showing.
  //
  // AN ABSENT FIELD STILL CONTRIBUTES NOTHING — a side with no dated field is unbounded — but
  // a gap no longer hides a date beyond it: an absent spud no longer conceals the permit.
  // ── THE BOUND CARRIES THE FIELD IT CAME FROM ──────────────────────────────────────────
  //
  // ⚠️ A CALLER CANNOT RE-DERIVE IT. The tearsheet used to name the bound by walking outward
  // to the NEAREST dated neighbour, which is the field the old implementation returned — but
  // this one returns the TIGHTEST. On a row with permit 2030, spud 2020, editing
  // completion) the bound is permit's 2030 while the nearest is spud, so the picker refused
  // 2021 and blamed "Spud date". Returning the field is the only way the two can agree.
  // ── ⚠️ MAX FIRST, THEN FILTER, THEN MIN ───────────────────────────────────────────────
  //
  // The two sides used to be computed independently, so a row whose own dates contradict
  // each other produced minMs > maxMs and the modal dropped BOTH bounds — which let a date
  // through that broke a neighbour outside the contradiction. With permit 2030, spud 2020
  // and first production 2021, editing completion, permit and first production contradict —
  // but spud 2020 still binds and a completion of 2019 should be refused.
  //
  // The upper bound is found first; any predecessor LATER than it is dropped as one of the
  // contradicting pair; the lower bound is the latest of what survives.
  //
  // ⚠️ THE SERVER DOES EXACTLY THIS, in LifecycleOrder.bounds (lifecycle_order.rb). The two must
  // agree: the client clamps the picker and the server refuses at Calculate, and a
  // disagreement is either a lock-out or a 422 the page did not predict. Keeping the same
  // three steps in the same order is what makes them one rule in two places.
  //
  // ⚠️ AND "the maximal mutually-consistent subset" IS NOT THE RULE, deliberately: on permit
  // 2030 / spud 2020 / first production 2040 it has two answers with different lower bounds,
  // and picking one invents a constraint. This has one answer, always.
  //
  // AN ORDERED ROW IS UNTOUCHED: no predecessor can be later than the earliest successor, so
  // nothing is dropped and both bounds are what they were.
  //
  // A completion derived from first production is first production's own date standing in
  // for one an older blob never carried, so it bounds nothing: counting it would pin first
  // production's lower bound to its current value. The server skips it the same way
  // (LifecycleOrder.base_values).
  const idx = order.indexOf(field);
  const msAt = (i: number) => {
    const date = dates[order[i]];
    return date?.source === 'derivedFromFirstProduction'
      ? null
      : parseIsoDay(date?.value);
  };

  let maxMs = Infinity;
  let maxField: MilestoneKey | undefined;
  for (let i = idx + 1; i < order.length; i++) {
    const ms = msAt(i);
    if (ms !== null && ms < maxMs) {
      maxMs = ms;
      maxField = order[i];
    }
  }

  let minMs = -Infinity;
  let minField: MilestoneKey | undefined;
  for (let i = 0; i < idx; i++) {
    const ms = msAt(i);
    // Later than the upper bound ⇒ this predecessor is one of the contradicting pair, and
    // it cannot bind anything. `maxMs` is Infinity when the side is unbounded, so nothing is
    // dropped then.
    if (ms !== null && ms <= maxMs && ms > minMs) {
      minMs = ms;
      minField = order[i];
    }
  }

  return { minMs, minField, maxMs, maxField };
}

/**
 * Clamps a value into an inclusive range.
 *
 * @param {number} value - The candidate.
 * @param {number} minMs - Lower bound (may be -Infinity).
 * @param {number} maxMs - Upper bound (may be +Infinity).
 * @return {number} The clamped value.
 */
export function clamp(value: number, minMs: number, maxMs: number): number {
  return Math.min(Math.max(value, minMs), maxMs);
}

/**
 * Whether shifting a milestone to `candidateMs` keeps lifecycle order against the
 * row's UNSHIFTED neighbors (the bulk-shift `outOfOrder` rule). Equal is allowed.
 *
 * @param {object} dates - The row's effective dates.
 * @param {MilestoneKey} field - The milestone being shifted.
 * @param {number} candidateMs - The proposed new value.
 * @param {MilestoneKey[]} order - The lifecycle order.
 * @return {boolean} True when the shift stays in order.
 */
export function shiftKeepsOrder(
  dates: Record<MilestoneKey, MergedDate>,
  field: MilestoneKey,
  candidateMs: number,
  order: MilestoneKey[] = MILESTONE_ORDER
): boolean {
  const { minMs, maxMs } = neighborBounds(dates, field, order);
  return candidateMs >= minMs && candidateMs <= maxMs;
}

/**
 * Clamps an ISO-day edit into the milestone's lifecycle-order neighbor bounds — the same
 * {@link neighborBounds} + {@link clamp} the chart's drag modifier applies before it
 * commits. The Table tab's date-cell edit routes through this so a picked day that would
 * break permit ≤ spud ≤ completion ≤ first production is snapped to the nearest neighbor
 * rather than landing an out-of-order override in the shared working log (one order rule,
 * every edit surface). Missing neighbors leave that side unbounded; an unparseable value
 * passes through untouched.
 *
 * @param {object} dates - The row's effective dates.
 * @param {MilestoneKey} field - The milestone being edited.
 * @param {string} iso - The picked ISO day.
 * @param {MilestoneKey[]} order - The lifecycle order (constraints.order).
 * @return {string} The clamped ISO day.
 */
export function clampIsoToNeighbors(
  dates: Record<MilestoneKey, MergedDate>,
  field: MilestoneKey,
  iso: string,
  order: MilestoneKey[] = MILESTONE_ORDER
): string {
  const ms = parseIsoDay(iso);
  if (ms === null) {
    return iso;
  }
  const { minMs, maxMs } = neighborBounds(dates, field, order);
  return toIsoDay(clamp(ms, minMs, maxMs));
}

// ---------------------------------------------------------------------------
// Phase segments
// ---------------------------------------------------------------------------

/**
 * The phase bars for one row's effective dates. A phase renders only when both its
 * bounding milestones exist (except "Producing", which is open-ended and runs from
 * first production to `producingEndMs`).
 *
 * @param {object} dates - Effective dates.
 * @param {number} producingEndMs - Right edge for the open-ended producing phase.
 * @return {PhaseSegment[]} The renderable segments.
 */
export function phaseSegments(
  dates: Record<MilestoneKey, MergedDate>,
  producingEndMs: number
): PhaseSegment[] {
  const segments: PhaseSegment[] = [];
  for (const phase of PHASE_ORDER) {
    const { start, end } = PHASE_BOUNDS[phase];
    const startMs = parseIsoDay(dates[start]?.value);
    if (startMs === null) {
      continue;
    }
    if (end === 'open') {
      if (producingEndMs > startMs) {
        segments.push({ phase, startMs, endMs: producingEndMs });
      }
      continue;
    }
    const endMs = parseIsoDay(dates[end]?.value);
    if (endMs === null || endMs < startMs) {
      continue;
    }
    segments.push({ phase, startMs, endMs });
  }
  return segments;
}

/**
 * Whether a row has a visible segment for the given phase (drives the phase filter).
 *
 * @param {object} dates - Effective dates.
 * @param {PhaseKey} phase - The phase to test.
 * @param {number} producingEndMs - Right edge for the producing phase.
 * @return {boolean} True when the phase renders on the row.
 */
export function rowHasPhase(
  dates: Record<MilestoneKey, MergedDate>,
  phase: PhaseKey,
  producingEndMs: number
): boolean {
  return phaseSegments(dates, producingEndMs).some((s) => s.phase === phase);
}

// ---------------------------------------------------------------------------
// Domain / axis window (with the pre-1900 garbage clamp)
// ---------------------------------------------------------------------------

/**
 * The axis window (start, end) in epoch ms across a set of effective-date maps.
 *
 * - future-only ON: the window opens at `effectiveDate`; only dates on/after it count
 *   toward the max.
 * - future-only OFF: the window opens at the earliest date on/after 1900-01-01; older
 *   "garbage" dates are excluded from the min/max (they pin to the start edge later).
 *
 * The end is padded by the producing tail so the open-ended producing bar has room.
 *
 * @param {Array} rowDates - Effective dates per row.
 * @param {boolean} futureOnly - The future-locations-only toggle.
 * @param {string} effectiveDate - The run's reference date (ISO).
 * @return {object} The axis window.
 */
export function domainRange(
  rowDates: Array<Record<MilestoneKey, MergedDate>>,
  futureOnly: boolean,
  effectiveDate: string
): { startMs: number; endMs: number } {
  const effMs = parseIsoDay(effectiveDate) ?? AXIS_MIN_MS;
  const floor = futureOnly ? effMs : AXIS_MIN_MS;
  // Running min/max in one pass — NOT `Math.min(...values)` / `Math.max(...values)`. A large
  // scenario has hundreds of thousands of milestone dates (turning future-only OFF widens the
  // floor so every one qualifies), and spreading that many args overflows the call stack
  // ("Maximum call stack size exceeded") — the crash on the future-only toggle for a big
  // scenario. A reduce-free loop also avoids allocating the whole array.
  let count = 0;
  let dataMin = Infinity;
  let dataMax = -Infinity;
  for (const dates of rowDates) {
    for (const field of MILESTONE_ORDER) {
      const ms = parseIsoDay(dates[field]?.value);
      if (ms !== null && ms >= floor) {
        count += 1;
        if (ms < dataMin) {
          dataMin = ms;
        }
        if (ms > dataMax) {
          dataMax = ms;
        }
      }
    }
  }
  const startMs = futureOnly ? effMs : count > 0 ? dataMin : AXIS_MIN_MS;
  const maxData = count > 0 ? dataMax : startMs;
  const endMs = moment
    .utc(maxData)
    .add(PRODUCING_TAIL_MONTHS, 'months')
    .valueOf();
  return { startMs, endMs: Math.max(endMs, startMs + 1) };
}

/**
 * The display x for a milestone: its true value, unless it predates the axis start
 * (a pre-1900 garbage date), in which case it pins to the start edge and reports
 * `clamped: true` so the tooltip can still show the true value.
 *
 * @param {number} trueMs - The true value in ms.
 * @param {number} axisStartMs - The axis start.
 * @return {object} The display x and clamp flag.
 */
export function clampDisplayMs(
  trueMs: number,
  axisStartMs: number
): { displayMs: number; clamped: boolean } {
  if (trueMs < axisStartMs) {
    return { displayMs: axisStartMs, clamped: true };
  }
  return { displayMs: trueMs, clamped: false };
}

/**
 * A human window label for the current axis range ("Jan 2018 – Jan 2036").
 *
 * @param {number} startMs - Window start.
 * @param {number} endMs - Window end.
 * @return {string} The label.
 */
export function windowLabel(startMs: number, endMs: number): string {
  return `${moment.utc(startMs).format('MMM YYYY')} – ${moment
    .utc(endMs)
    .format('MMM YYYY')}`;
}

/**
 * Projects an epoch-ms value onto the shared time domain as a percentage (0–100), the
 * `left` / `width` a DOM Gantt track positions its phase bars, milestone markers, and
 * year gridlines with. Clamped to [0, 100] so a pre-1900 garbage date (or the open-ended
 * producing tail) pins to an edge instead of overflowing the track; a degenerate domain
 * (`endMs <= startMs`) collapses everything to 0. Inverse of the drag hit-test's
 * pixel→date read (`startMs + pct/100 * (endMs - startMs)`).
 *
 * @param {number} ms - The value to project.
 * @param {number} startMs - The domain start (0%).
 * @param {number} endMs - The domain end (100%).
 * @return {number} The clamped percentage in [0, 100].
 */
export function toPercent(ms: number, startMs: number, endMs: number): number {
  if (endMs <= startMs) {
    return 0;
  }
  const pct = ((ms - startMs) / (endMs - startMs)) * 100;
  return Math.min(100, Math.max(0, pct));
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/**
 * Whether a row matches the free-text search (well name / API number / slot id),
 * case-insensitive.
 *
 * @param {ScheduleRow} row - The row.
 * @param {string} query - The search text.
 * @return {boolean} Match.
 */
export function matchesSearch(row: ScheduleRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    row.wellName.toLowerCase().includes(q) ||
    (row.apiNumber?.toLowerCase().includes(q) ?? false) ||
    row.slotId.toLowerCase().includes(q)
  );
}

/**
 * Whether a row has any milestone on/after the effective date (future-only filter).
 *
 * @param {object} dates - Effective dates.
 * @param {number} effectiveMs - The effective date in ms.
 * @return {boolean} True when the row has a future milestone.
 */
export function rowIsFuture(
  dates: Record<MilestoneKey, MergedDate>,
  effectiveMs: number
): boolean {
  return MILESTONE_ORDER.some((field) => {
    const ms = parseIsoDay(dates[field]?.value);
    return ms !== null && ms >= effectiveMs;
  });
}

// ---------------------------------------------------------------------------
// Row model (collapse / expand + filters → ordered bands)
// ---------------------------------------------------------------------------

/** The dialog's shared filter inputs (search + phase + future-only). */
export interface ScheduleFilterOptions {
  futureOnly: boolean;
  effectiveDate: string;
  search: string;
  phaseFilter: PhaseKey | null;
}

export interface RowModelOptions extends ScheduleFilterOptions {
  /** Area group ids currently expanded (numeric ids). */
  expandedGroupIds: Set<number>;
  /** Whether the "Unscheduled" pseudo-group is expanded. */
  unscheduledExpanded: boolean;
}

/**
 * The dialog's ONE shared filtered row model (spec 5.1): applies the search / phase /
 * future-only filter to the payload rows over their EFFECTIVE dates (base overlaid with
 * the working log), returning each passing row paired with those effective dates. Both
 * the chart's {@link buildRowModel} and the Table tab call this — one filter
 * implementation, two callers, so a filter change moves both views together.
 *
 * @param {ScheduleRow[]} rows - The full payload row set.
 * @param {ScheduleOverrideEntry[]} entries - The working-copy overrides (overlaid first).
 * @param {ScheduleFilterOptions} options - The search / phase / future-only filter state.
 * @return {FilteredScheduleRow[]} The passing rows with their effective dates.
 */
export function filterScheduleRows(
  rows: ScheduleRow[],
  entries: ScheduleOverrideEntry[],
  options: ScheduleFilterOptions
): FilteredScheduleRow[] {
  const effectiveMs = parseIsoDay(options.effectiveDate) ?? AXIS_MIN_MS;
  const withDates: FilteredScheduleRow[] = rows.map((row) => ({
    row,
    dates: overlayRowDates(row, entries),
  }));
  // A generous producing end for phase-filter tests — the full-set domain end.
  const producingEnd = domainRange(
    withDates.map((entry) => entry.dates),
    options.futureOnly,
    options.effectiveDate
  ).endMs;

  return withDates.filter(({ row, dates }) => {
    if (!matchesSearch(row, options.search)) {
      return false;
    }
    if (options.futureOnly && !rowIsFuture(dates, effectiveMs)) {
      return false;
    }
    if (
      options.phaseFilter &&
      !rowHasPhase(dates, options.phaseFilter, producingEnd)
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Builds the ordered band model for the current expand/collapse + filter state:
 * row groups ordered by slot count desc with "Unscheduled" always last, each group
 * either expanded to one band per passing slot or collapsed to a single min–max summary
 * band. `shownRowCount` / `totalRowCount` back the truthful "N of M shown" readout.
 * The grouping dimension is endpoint-specific (units on the DPS preview, areas on the
 * eval schedule) — this function only ever reads the opaque `groupId`.
 *
 * @param {ScheduleRow[]} rows - The full payload row set.
 * @param {ScheduleGroup[]} groups - The payload row groups (already count-desc; re-sorted defensively).
 * @param {ScheduleOverrideEntry[]} entries - The working-copy overrides (overlaid before filtering).
 * @param {RowModelOptions} options - Expand/collapse + filter state.
 * @return {ScheduleRowModel} The band model.
 */
export function buildRowModel(
  rows: ScheduleRow[],
  groups: ScheduleGroup[],
  entries: ScheduleOverrideEntry[],
  options: RowModelOptions
): ScheduleRowModel {
  // The dialog's ONE shared filtered row model (also feeds the Table tab).
  const filtered = filterScheduleRows(rows, entries, options);
  const passing = filtered.map((entry) => entry.row);
  const effByRow = new Map<SlotId, Record<MilestoneKey, MergedDate>>(
    filtered.map((entry) => [entry.row.slotId, entry.dates])
  );

  const passingByGroup = new Map<number | 'unscheduled', ScheduleRow[]>();
  for (const row of passing) {
    // Group by the row's group (unit on the DPS preview, area on the eval schedule).
    // Unscheduled paceable rows keep the "Unscheduled" pseudo-group regardless of group;
    // scheduled rows group under their groupId. A row with no group (a pdp/context row,
    // or a slot matched to nothing) can't be placed, so it also falls to "Unscheduled".
    const key = row.unscheduled
      ? 'unscheduled'
      : (row.groupId ?? 'unscheduled');
    const list = passingByGroup.get(key) ?? [];
    list.push(row);
    passingByGroup.set(key, list);
  }

  // Group order: numeric groups by their SHOWN (filtered) row count desc — the count the
  // header displays and an expand reveals — then "Unscheduled" last. Ordering by the shown
  // count (not the payload's unfiltered `slotCount`) keeps a filtered header from claiming
  // more than it shows ("163 properties" that expands to 3 under future-only) and keeps the
  // displayed counts in descending order.
  const orderedGroups = [...groups].sort(
    (a, b) =>
      (passingByGroup.get(b.id)?.length ?? 0) -
      (passingByGroup.get(a.id)?.length ?? 0)
  );

  const visibleRows: VisibleRow[] = [];
  const summaryBands: GroupSummaryBand[] = [];
  const groupHeaders: GroupHeader[] = [];
  const bandLabels: string[] = [];
  let bandIndex = 0;

  const addGroup = (
    groupId: number | null,
    name: string,
    slotCount: number,
    groupRows: ScheduleRow[],
    expanded: boolean
  ) => {
    if (groupRows.length === 0) {
      return;
    }
    // A per-group expand chevron, anchored at the group's first band (INS-10671).
    groupHeaders.push({ groupId, name, slotCount, bandIndex, expanded });
    if (expanded) {
      for (const row of groupRows) {
        const dates = effByRow.get(row.slotId) as Record<
          MilestoneKey,
          MergedDate
        >;
        const fullyLocked = rowIsFullyLocked(dates);
        // The lock is rendered as a Carbon glyph in the DOM left gutter, driven by
        // `fullyLocked` — no 🔒 in the label string (INS-10671, item 6).
        const label = row.wellName;
        visibleRows.push({ row, bandIndex, label, fullyLocked, dates });
        bandLabels.push(label);
        bandIndex += 1;
      }
    } else {
      const { minMs, maxMs } = groupSpan(groupRows, effByRow);
      const label = `${name} (${slotCount})`;
      summaryBands.push({
        groupId,
        name,
        bandIndex,
        slotCount,
        minMs,
        maxMs,
      });
      bandLabels.push(label);
      bandIndex += 1;
    }
  };

  for (const group of orderedGroups) {
    const groupRows = passingByGroup.get(group.id) ?? [];
    // The header/summary count is the SHOWN row count, so it matches the rows an expand
    // reveals (and the "Unscheduled" group, which already uses its shown length) rather than
    // the payload's unfiltered `slotCount` (Defect: "163 properties" expanding to 3).
    addGroup(
      group.id,
      group.name,
      groupRows.length,
      groupRows,
      options.expandedGroupIds.has(group.id)
    );
  }

  // "Unscheduled" pseudo-group always last.
  const unscheduledRows = passingByGroup.get('unscheduled') ?? [];
  addGroup(
    null,
    UNSCHEDULED_GROUP_NAME,
    unscheduledRows.length,
    unscheduledRows,
    options.unscheduledExpanded
  );

  const groupCount = new Set(
    rows
      .filter((r) => !r.unscheduled)
      .map((r) => r.groupId)
      .filter((id): id is number => id !== null)
  ).size;

  return {
    visibleRows,
    summaryBands,
    groupHeaders,
    bandLabels,
    shownRowCount: passing.length,
    totalRowCount: rows.length,
    groupCount,
  };
}

/**
 * The min/max milestone ms across a group's rows (for a collapsed summary band).
 *
 * @param {ScheduleRow[]} groupRows - Rows in the group.
 * @param {Map} effByRow - Effective dates.
 * @return {object} The span.
 */
function groupSpan(
  groupRows: ScheduleRow[],
  effByRow: Map<SlotId, Record<MilestoneKey, MergedDate>>
): { minMs: number | null; maxMs: number | null } {
  let minMs: number | null = null;
  let maxMs: number | null = null;
  for (const row of groupRows) {
    const dates = effByRow.get(row.slotId);
    if (!dates) {
      continue;
    }
    for (const field of MILESTONE_ORDER) {
      const ms = parseIsoDay(dates[field]?.value);
      if (ms === null || ms < AXIS_MIN_MS) {
        continue;
      }
      minMs = minMs === null ? ms : Math.min(minMs, ms);
      maxMs = maxMs === null ? ms : Math.max(maxMs, ms);
    }
  }
  return { minMs, maxMs };
}

/**
 * The milestone tooltip label ("Permit — Apr 2027", "Spud — Apr 2027 (reported —
 * locked)").
 *
 * @param {MilestoneKey} field - The milestone.
 * @param {MergedDate} date - Its merged date.
 * @return {string} The label.
 */
export function milestoneTooltipLabel(
  field: MilestoneKey,
  date: MergedDate
): string {
  const when = date.value
    ? moment.utc(date.value).format('MMM YYYY')
    : 'unscheduled';
  const suffix = date.locked ? ' (reported — locked)' : '';
  return `${MILESTONE_LABELS[field]} — ${when}${suffix}`;
}

// ---------------------------------------------------------------------------
// DOM row labels (INS-10671 restyle)
// ---------------------------------------------------------------------------

/**
 * The two-line row label the DOM left gutter shows for an expanded slot (INS-10671):
 * line 1 = the well name, falling back to the composite slot id when there is no well
 * name (eval 1694 is mostly LTD slots with no name); line 2 = `API · formation` from
 * `apiNumber` + `interval`, omitting whichever piece is null and dropping line 2 entirely
 * when both are absent.
 *
 * @param {ScheduleRow} row - The payload row.
 * @return {object} `{ line1, line2 }` — line2 is null when there is no API/formation.
 */
export function rowLabelLines(row: ScheduleRow): {
  line1: string;
  line2: string | null;
} {
  const line1 = row.wellName?.trim() ? row.wellName : row.slotId;
  const parts = [row.apiNumber, row.interval].filter(
    (part): part is string => typeof part === 'string' && part.trim() !== ''
  );
  return { line1, line2: parts.length > 0 ? parts.join(' · ') : null };
}
